import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './tasks-ehr-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';

/* ── EhrInfoTip — ⓘ tooltip next to page title ── */
const EhrInfoTip = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [tipStyle, setTipStyle] = useState({});

  const handleEnter = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const spaceRight = window.innerWidth - rect.left;
    const spaceBelow = window.innerHeight - rect.bottom;

    setTipStyle({
      position: 'fixed',
      left: spaceRight < 320 ? undefined : `${rect.left}px`,
      right: spaceRight < 320 ? '16px' : undefined,
      top: spaceBelow < 160 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 160 ? `${window.innerHeight - rect.top + 6}px` : undefined,
      maxWidth: '320px',
      whiteSpace: 'normal',
    });
    setOpen(true);
  };

  return (
    <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)} style={{ cursor: 'help', marginLeft: '8px', color: 'var(--color-text-muted)' }}>
      <i className="fe fe-info" style={{ fontSize: 'var(--font-size-sm)' }} />
      {open && (
        <span className="na-type-popover" style={tipStyle} role="tooltip">
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>About EHR Tasks</span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>
            This page displays pending resident changes received from the integrated EHR system. Each task must be reviewed and approved or rejected before it takes effect in PalCare.
          </span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)', marginTop: '4px' }}>Move-in Resident</span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>
            New residents scheduled to move in. Some may be pre-scheduled by the EHR and cannot be approved until the resident is officially allocated their area on the move-in date.
          </span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)', marginTop: '4px' }}>Relocate Resident</span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>
            Residents being moved from one area to another within the campus.
          </span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)', marginTop: '4px' }}>Move-out Resident</span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>
            Residents that have been discharged or moved out in the EHR and need to be marked inactive in PalCare.
          </span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)', marginTop: '4px' }}>Update Resident Info</span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>
            Changes to resident details (name, phone, email, care level, etc.) that originated in the EHR system.
          </span>
        </span>
      )}
    </span>
  );
};

import EhrTaskTable, {
  ADD_COLUMN_DEFS,
  EDIT_COLUMN_DEFS,
  MOVE_COLUMN_DEFS,
  REMOVE_COLUMN_DEFS,
} from './components/EhrTaskTable.jsx';
import ApproveAddDialog from './components/ApproveAddDialog.jsx';
import ApproveAddUserDialog from './components/ApproveAddUserDialog.jsx';
import ConfirmActionDialog from './components/ConfirmActionDialog.jsx';
// Shared components
import ActiveFilterBar from '../../community/residents/components/ActiveFilterBar.jsx';
import TableSkeleton from '../../community/residents/components/TableSkeleton.jsx';
// V2 data layer
import {
  getSession, getCampusId, getUserId, getSessionId, getCampusConfig,
  getCampusList, updateSessionCampus, getUsername, getEhrInfo, setEhrStgCount,
} from '../../../services/session.js';
import {
  fetchStagedTasks,
  fetchResidentGroups,
  approveAddTask,
  approveEditTask,
  approveMoveTask,
  approveRemoveTask,
  rejectTask,
  fetchStagedUsers,
  rejectUserTask,
} from './api/tasksEhrApi.js';
import {
  fetchUserGroupList,
  fetchRoles,
  addUser,
  deleteUsers,
} from '../../users/api/usersApi.js';
import { convertIsoToLocalDate } from '../../../libs.jsx';

// ---------------------------------------------------------------------------
// Tab configuration
// ---------------------------------------------------------------------------
const RESIDENT_TABS = [
  { key: 'add',    label: 'Move-in Resident',     emptyMsg: 'No move-in tasks to approve' },
  { key: 'move',   label: 'Relocate Resident',    emptyMsg: 'No relocating tasks to approve' },
  { key: 'remove', label: 'Move-out Resident',    emptyMsg: 'No move-out tasks to approve' },
  { key: 'edit',   label: 'Update Resident Info',  emptyMsg: 'No update tasks to approve' },
];

const USER_TABS = [
  { key: 'user-add',    label: 'Add User',    emptyMsg: 'No add user tasks to approve' },
  { key: 'user-remove', label: 'Remove User', emptyMsg: 'No remove user tasks to approve' },
];

const ALL_TABS = [...RESIDENT_TABS, ...USER_TABS];
const USER_TAB_KEYS = USER_TABS.map((t) => t.key);

/** Created On — auto-generated MySQL UTC timestamp, rendered campus-local. */
const CREATED_ON_COLUMN = { key: 'create_time', label: 'Created On', sortable: true, filterable: false, type: 'datetime-utc' };

/** User staged columns */
const USER_ADD_COLUMN_DEFS = [
  { key: 'display_name',  label: 'Display Name', sortable: true, filterable: true },
  { key: 'email',         label: 'Email',        sortable: true, filterable: true },
  { key: 'phone',         label: 'Phone',        sortable: true, filterable: true },
  { key: 'ehr_job_title', label: 'Title',        sortable: true, filterable: true },
  CREATED_ON_COLUMN,
];

const USER_REMOVE_COLUMN_DEFS = [
  { key: 'display_name', label: 'Display Name', sortable: true, filterable: true },
  { key: 'email',        label: 'Email',        sortable: true, filterable: true },
  { key: 'username',     label: 'Username',     sortable: true, filterable: true },
  CREATED_ON_COLUMN,
];

/** Map tab key -> column definitions */
const TAB_COLUMNS = {
  add: ADD_COLUMN_DEFS,
  edit: EDIT_COLUMN_DEFS,
  move: MOVE_COLUMN_DEFS,
  remove: REMOVE_COLUMN_DEFS,
  'user-add':    USER_ADD_COLUMN_DEFS,
  'user-remove': USER_REMOVE_COLUMN_DEFS,
};

/** Map tab key -> search keys */
const TAB_SEARCH_KEYS = {
  add:    ['resident_first_name', 'resident_last_name', 'resident_nickname', 'resident_phone', 'resident_email', 'area_name'],
  edit:   ['resident_first_name', 'resident_last_name', 'resident_nickname', 'resident_phone', 'resident_email', 'area_name'],
  move:   ['resident_name', 'old_area_name', 'area_name'],
  remove: ['resident_name', 'area_name'],
  'user-add':    ['display_name', 'email', 'phone', 'ehr_job_title', 'username'],
  'user-remove': ['display_name', 'email', 'username'],
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TasksEhrPage -- V2 EHR staged-task approval page.
 *
 * Displays 4 tabs of pending EHR tasks (Move-in, Relocate, Move-out,
 * Update Info) with search, column filters, sorting, and pagination.
 * Users click a row to open an approval dialog and approve or reject.
 */
const TasksEhrPage = () => {
  // ==========================================================================
  // SESSION
  // ==========================================================================
  const [campusId, setCampusId] = useState(() => getCampusId());
  const campusList = useMemo(() => getCampusList() || [], []);
  const campusName = useMemo(() => {
    const session = getSession();
    if (!session) return '';
    const cfg = session.campusData?.[campusId.toString()];
    return cfg?.name || session.campus || '';
  }, [campusId]);
  const campusConfig = useMemo(() => {
    const s = getSession();
    return s?.campusData?.[campusId.toString()] ?? null;
  }, [campusId]);

  // Campus UTC offset for rendering UTC timestamps (e.g. create_time) as local.
  const tzOffset = useMemo(() => parseInt(campusConfig?.tz_offset ?? 0, 10) || 0, [campusConfig]);

  /** Session params for API write calls */
  const getSessionParams = useCallback(() => ({
    campusId,
    userId: getUserId(),
    clientId: getSessionId(),
  }), [campusId]);

  /** Campus change: check ehr_active, redirect to / if inactive */
  const handleCampusChange = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    if (!getEhrInfo(val).active) {
      updateSessionCampus(val);
      window.location.href = '/';
      return;
    }
    setCampusId(val);
    updateSessionCampus(val);
  }, []);

  // ==========================================================================
  // TOAST
  // ==========================================================================
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // ==========================================================================
  // DATA STATE
  // ==========================================================================
  const [addData, setAddData] = useState([]);
  const [editData, setEditData] = useState([]);
  const [moveData, setMoveData] = useState([]);
  const [removeData, setRemoveData] = useState([]);
  const [groups, setGroups] = useState([]);
  // Staged user tasks
  const [userAddData, setUserAddData] = useState([]);
  const [userRemoveData, setUserRemoveData] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Parent-tab gating — driven by the EHR vendor's receiving flags
  // (is_receiving → resident data, is_receiving_user → user data).
  const ehrInfo = getEhrInfo(campusId);
  const showResidentsTab = !!ehrInfo.receiving_resident;
  const showUsersTab = !!ehrInfo.receiving_user;

  // Shared controls
  const [activeTab, setActiveTab] = useState(showResidentsTab ? 'add' : 'user-add');
  const parentTab = USER_TAB_KEYS.includes(activeTab) ? 'users' : 'residents';
  const [search, setSearch] = useState('');
  const [perPage, setPerPage] = usePersistedPerPage();

  // Per-tab filter / sort / pagination
  const [tabState, setTabState] = useState({
    add:           { filters: {}, sortColumn: 'resident_first_name', sortOrder: 'asc', page: 1 },
    edit:          { filters: {}, sortColumn: 'resident_first_name', sortOrder: 'asc', page: 1 },
    move:          { filters: {}, sortColumn: 'resident_name',       sortOrder: 'asc', page: 1 },
    remove:        { filters: {}, sortColumn: 'resident_name',       sortOrder: 'asc', page: 1 },
    'user-add':    { filters: {}, sortColumn: 'display_name',        sortOrder: 'asc', page: 1 },
    'user-remove': { filters: {}, sortColumn: 'display_name',        sortOrder: 'asc', page: 1 },
  });

  /** Helper: update one key in the active tab's state slice */
  const updateTabState = useCallback((key, value) => {
    setTabState((prev) => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], [key]: value },
    }));
  }, [activeTab]);

  // Dialog state
  const [activeDialog, setActiveDialog] = useState(null);     // 'add'|'edit'|'move'|'remove'|'reject' | null
  const [selectedTask, setSelectedTask] = useState(null);      // row object
  const [previousDialog, setPreviousDialog] = useState(null);  // for reject->return flow
  const [submitting, setSubmitting] = useState(false);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const tasks = [];
      if (showResidentsTab) {
        tasks.push(fetchStagedTasks(campusId));
        tasks.push(fetchResidentGroups(campusId));
      } else {
        tasks.push(Promise.resolve(null));
        tasks.push(Promise.resolve(null));
      }
      if (showUsersTab) {
        tasks.push(fetchStagedUsers(campusId));
        tasks.push(fetchUserGroupList(campusId));
        tasks.push(fetchRoles());
      } else {
        tasks.push(Promise.resolve(null));
        tasks.push(Promise.resolve(null));
        tasks.push(Promise.resolve(null));
      }

      const [staged, grps, stagedUsers, uGroups, rolesData] = await Promise.all(tasks);

      const addList    = staged?.length > 0 ? staged[0] : [];
      const editList   = staged?.length > 1 ? staged[1] : [];
      const moveList   = staged?.length > 2 ? staged[2] : [];
      const removeList = staged?.length > 3 ? staged[3] : [];
      setAddData(addList);
      setEditData(editList);
      setMoveData(moveList);
      setRemoveData(removeList);
      setGroups(grps || []);

      const userAddList    = stagedUsers?.length > 0 ? stagedUsers[0] : [];
      const userRemoveList = stagedUsers?.length > 1 ? stagedUsers[1] : [];
      setUserAddData(userAddList);
      setUserRemoveData(userRemoveList);
      setUserGroups(uGroups || []);
      setRoles(rolesData || []);

      // Update session EHR count (combined residents + users) on the EHR vendor
      // so the nav badge stays in sync without a full reload.
      const total =
        addList.length + editList.length + moveList.length + removeList.length +
        userAddList.length + userRemoveList.length;
      setEhrStgCount(campusId, total);
    } catch (err) {
      console.error('[TasksEhrPage] Load error:', err);
      showToast('error', 'Failed to load EHR tasks');
    } finally {
      setLoading(false);
    }
  }, [campusId, showToast, showResidentsTab, showUsersTab]);

  // Initial load
  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh every 600 seconds (10 minutes)
  useEffect(() => {
    const interval = setInterval(() => { loadData(); }, 600000);
    return () => clearInterval(interval);
  }, [loadData]);

  // ==========================================================================
  // RAW DATA LOOKUP
  // ==========================================================================
  const rawDataForTab = useMemo(() => ({
    add: addData,
    edit: editData,
    move: moveData,
    remove: removeData,
    'user-add': userAddData,
    'user-remove': userRemoveData,
  }), [addData, editData, moveData, removeData, userAddData, userRemoveData]);

  /** Combined counts for parent tabs */
  const residentsTotal = addData.length + editData.length + moveData.length + removeData.length;
  const usersTotal = userAddData.length + userRemoveData.length;

  // ==========================================================================
  // FILTER / SORT / PAGINATE — active tab only
  // ==========================================================================
  const currentTab = tabState[activeTab];

  const filtered = useMemo(() => {
    let result = [...(rawDataForTab[activeTab] || [])];
    const searchKeys = TAB_SEARCH_KEYS[activeTab];

    // 1. Text search
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter((row) =>
        searchKeys.some((key) => {
          const val = row[key];
          return val != null && String(val).toLowerCase().includes(s);
        })
      );
    }

    // 2. Column filters
    Object.entries(currentTab.filters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      result = result.filter((row) => {
        const cellVal = row[columnKey];
        return values.includes(cellVal != null && cellVal !== '' ? String(cellVal) : '(empty)');
      });
    });

    // 3. Sort
    result.sort((a, b) => {
      let aVal = a[currentTab.sortColumn];
      let bVal = b[currentTab.sortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      return currentTab.sortOrder === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [rawDataForTab, activeTab, search, currentTab.filters, currentTab.sortColumn, currentTab.sortOrder]);

  // Pagination
  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
  const safePage = Math.min(currentTab.page, totalPages);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, safePage, perPage]);

  const showingStart = totalFiltered === 0 ? 0 : (safePage - 1) * perPage + 1;
  const showingEnd = Math.min(safePage * perPage, totalFiltered);

  // Reset page on search/filter change
  useEffect(() => { updateTabState('page', 1); }, [search, currentTab.filters, perPage]);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [safePage, totalPages]);

  /** Column labels for ActiveFilterBar */
  const columnLabels = useMemo(() => {
    const map = {};
    TAB_COLUMNS[activeTab].forEach((c) => { map[c.key] = c.label; });
    return map;
  }, [activeTab]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  const handleSort = useCallback((key) => {
    setTabState((prev) => {
      const ts = prev[activeTab];
      if (ts.sortColumn === key) {
        return {
          ...prev,
          [activeTab]: { ...ts, sortOrder: ts.sortOrder === 'asc' ? 'desc' : 'asc' },
        };
      }
      return {
        ...prev,
        [activeTab]: { ...ts, sortColumn: key, sortOrder: 'asc' },
      };
    });
  }, [activeTab]);

  const handleFilterChange = useCallback((columnKey, selectedValues) => {
    setTabState((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        filters: { ...prev[activeTab].filters, [columnKey]: selectedValues },
      },
    }));
  }, [activeTab]);

  const handleRemoveFilter = useCallback((columnKey) => {
    setTabState((prev) => {
      const next = { ...prev[activeTab].filters };
      delete next[columnKey];
      return { ...prev, [activeTab]: { ...prev[activeTab], filters: next } };
    });
  }, [activeTab]);

  const handleClearAllFilters = useCallback(() => {
    setTabState((prev) => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], filters: {} },
    }));
  }, [activeTab]);

  // Row click -> open dialog (blocked for status 15 pending move-ins)
  const handleRowClick = useCallback((row) => {
    if (row.status === 15) return;
    setSelectedTask(row);
    setActiveDialog(activeTab);
    setPreviousDialog(null);
  }, [activeTab]);

  /** Switch parent tab → reset active subtab to first one in that group */
  const handleParentTabChange = useCallback((p) => {
    setActiveTab(p === 'residents' ? 'add' : 'user-add');
    setSearch('');
  }, []);

  // -- APPROVE handlers --

  const handleApproveAdd = useCallback(async (task, groupIds) => {
    setSubmitting(true);
    try {
      const session = getSessionParams();
      const payload = {
        cloud_id: task.cloud_id,
        campus_id: campusId,
        resident_first_name: task.resident_first_name,
        resident_last_name: task.resident_last_name,
        resident_phone: task.resident_phone,
        resident_email: task.resident_email,
        resident_nickname: task.resident_nickname,
        ehr_level_of_care: task.ehr_level_of_care_id,
        is_area_assigned: true,
        area_id: task.area_id,
        resident_group_ids: groupIds,
        move_in_date: task.physical_move_in_date,
        ehr_external_id: task.ehr_resident_id,
        ehr_campus_id: task.ehr_campus_id,
        modified_by: session.userId,
        vendor_id: getEhrInfo(campusId).vendor_id,
      };
      const result = await approveAddTask(payload, session);
      if (result && result[0] === true) {
        showToast('success', `${task.resident_first_name} ${task.resident_last_name} has been added`);
        setActiveDialog(null);
        setSelectedTask(null);
        loadData();
      } else {
        showToast('error', `Error during add: ${result?.[1] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[TasksEhrPage] Approve add error:', err);
      showToast('error', 'Failed to approve add task');
    } finally {
      setSubmitting(false);
    }
  }, [campusId, getSessionParams, showToast, loadData]);

  const handleApproveEdit = useCallback(async () => {
    if (!selectedTask) return;
    setSubmitting(true);
    try {
      const session = getSessionParams();
      const payload = {
        cloud_id: selectedTask.cloud_id,
        resident_id: selectedTask.tmp_resident_id,
        campus_id: campusId,
        resident_first_name: selectedTask.resident_first_name,
        resident_last_name: selectedTask.resident_last_name,
        resident_nickname: selectedTask.resident_nickname,
        ehr_level_of_care: selectedTask.ehr_level_of_care_id,
        resident_phone: selectedTask.resident_phone,
        resident_email: selectedTask.resident_email,
        is_area_assigned: true,
        area_id: selectedTask.area_id,
        move_in_date: selectedTask.physical_move_in_date,
        ehr_external_id: selectedTask.ehr_resident_id,
        modified_by: session.userId,
      };
      const result = await approveEditTask(payload, session);
      if (result && result[0]) {
        showToast('success', `${selectedTask.resident_first_name} ${selectedTask.resident_last_name} updated`);
        setActiveDialog(null);
        setSelectedTask(null);
        loadData();
      } else {
        showToast('error', `Error during update: ${result?.[1] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[TasksEhrPage] Approve edit error:', err);
      showToast('error', 'Failed to approve edit task');
    } finally {
      setSubmitting(false);
    }
  }, [selectedTask, campusId, getSessionParams, showToast, loadData]);

  const handleApproveMove = useCallback(async () => {
    if (!selectedTask) return;
    setSubmitting(true);
    try {
      const session = getSessionParams();
      const payload = {
        cloud_id: selectedTask.cloud_id,
        resident_id: selectedTask.tmp_resident_id,
        area_id: selectedTask.area_id,
        modified_by: session.userId,
      };
      const oldPayload = {
        resident_id: selectedTask.tmp_resident_id,
        area_id: selectedTask.old_area_id,
      };
      const result = await approveMoveTask(payload, oldPayload, session);
      if (result && result[0]) {
        showToast('success', `Resident moved successfully to ${selectedTask.area_name}`);
        setActiveDialog(null);
        setSelectedTask(null);
        loadData();
      } else {
        showToast('error', `Error during move: ${result?.[1] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[TasksEhrPage] Approve move error:', err);
      showToast('error', 'Failed to approve move task');
    } finally {
      setSubmitting(false);
    }
  }, [selectedTask, getSessionParams, showToast, loadData]);

  const handleApproveRemove = useCallback(async () => {
    if (!selectedTask) return;
    setSubmitting(true);
    try {
      const session = getSessionParams();
      const payload = {
        cloud_id: selectedTask.cloud_id,
        resident_id: selectedTask.tmp_resident_id,
        move_out_date: selectedTask.physical_move_out_date,
        modified_by: session.userId,
      };
      const result = await approveRemoveTask(payload, session);
      if (result && result[0]) {
        showToast('success', `${selectedTask.resident_name} has been marked in-active`);
        setActiveDialog(null);
        setSelectedTask(null);
        loadData();
      } else {
        showToast('error', `Error during remove: ${result?.[1] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[TasksEhrPage] Approve remove error:', err);
      showToast('error', 'Failed to approve remove task');
    } finally {
      setSubmitting(false);
    }
  }, [selectedTask, getSessionParams, showToast, loadData]);

  // -- REJECT handlers --

  /** Open the reject confirmation dialog (from within any approve dialog) */
  const handleOpenReject = useCallback((task) => {
    setPreviousDialog(activeDialog);
    setActiveDialog('reject');
  }, [activeDialog]);

  /** Confirm rejection — dispatches to resident or user reject based on previousDialog */
  const handleConfirmReject = useCallback(async () => {
    if (!selectedTask) return;
    const isUser = previousDialog === 'user-add' || previousDialog === 'user-remove';
    setSubmitting(true);
    try {
      const session = getSessionParams();
      const result = isUser
        ? await rejectUserTask(selectedTask.cloud_id, session)
        : await rejectTask(selectedTask.cloud_id, session);
      if (result && result[0]) {
        const typeLabel =
          previousDialog === 'add' ? 'Move-in'
          : previousDialog === 'edit' ? 'Update'
          : previousDialog === 'move' ? 'Relocate'
          : previousDialog === 'remove' ? 'Move-out'
          : previousDialog === 'user-add' ? 'Add user'
          : 'Remove user';
        const name = isUser
          ? (selectedTask.display_name || selectedTask.username || 'task')
          : (selectedTask.resident_name || `${selectedTask.resident_first_name} ${selectedTask.resident_last_name}`);
        showToast('success', `${typeLabel} task for ${name} has been removed`);
        setActiveDialog(null);
        setSelectedTask(null);
        setPreviousDialog(null);
        loadData();
      } else {
        showToast('error', `Error during rejection: ${result?.[1] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[TasksEhrPage] Reject error:', err);
      showToast('error', 'Failed to reject task');
    } finally {
      setSubmitting(false);
    }
  }, [selectedTask, previousDialog, getSessionParams, showToast, loadData]);

  /** Cancel rejection -> return to previous dialog */
  const handleCancelReject = useCallback(() => {
    setActiveDialog(previousDialog);
    setPreviousDialog(null);
  }, [previousDialog]);

  // -- USER approval handlers --

  /** Approve a staged "Add User" task (action: new-task on usersApi) */
  const handleApproveUserAdd = useCallback(async (formData) => {
    if (!selectedTask) return;
    setSubmitting(true);
    try {
      const session = getSessionParams();
      const result = await addUser(formData, session, true);
      if (result && result[0] === true) {
        showToast('success', `${formData.display_name} has been added`);
        setActiveDialog(null);
        setSelectedTask(null);
        loadData();
      } else {
        showToast('error', `Error during user add: ${result?.[1] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[TasksEhrPage] Approve user add error:', err);
      showToast('error', 'Failed to approve add user task');
    } finally {
      setSubmitting(false);
    }
  }, [selectedTask, getSessionParams, showToast, loadData]);

  /** Approve a staged "Remove User" task (action: remove-task on usersApi) */
  const handleApproveUserRemove = useCallback(async () => {
    if (!selectedTask) return;
    setSubmitting(true);
    try {
      const session = getSessionParams();
      const result = await deleteUsers([{
        user_id: selectedTask.user_id,
        username: selectedTask.username,
        cognito_id: selectedTask.cognito_id,
        cloud_id: selectedTask.cloud_id,
      }], { ...session, username: getUsername() }, true);
      if (result && result[0] === true) {
        showToast('success', `${selectedTask.display_name || selectedTask.username} has been removed`);
        setActiveDialog(null);
        setSelectedTask(null);
        loadData();
      } else {
        showToast('error', `Error during user remove: ${result?.[1] || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[TasksEhrPage] Approve user remove error:', err);
      showToast('error', 'Failed to approve remove user task');
    } finally {
      setSubmitting(false);
    }
  }, [selectedTask, getSessionParams, showToast, loadData]);


  /** Close any dialog */
  const handleCloseDialog = useCallback(() => {
    setActiveDialog(null);
    setSelectedTask(null);
    setPreviousDialog(null);
  }, []);

  // ==========================================================================
  // RENDER HELPERS
  // ==========================================================================

  /** Render the dialog body for edit approval */
  const renderEditBody = () => {
    if (!selectedTask) return null;
    return (
      <div className="ehr-dialog__field-grid">
        <div className="ehr-dialog__field">
          <span className="ehr-dialog__field-label">First Name</span>
          <span className="ehr-dialog__field-value">{selectedTask.resident_first_name || '\u2014'}</span>
        </div>
        <div className="ehr-dialog__field">
          <span className="ehr-dialog__field-label">Last Name</span>
          <span className="ehr-dialog__field-value">{selectedTask.resident_last_name || '\u2014'}</span>
        </div>
        <div className="ehr-dialog__field">
          <span className="ehr-dialog__field-label">Nickname</span>
          <span className="ehr-dialog__field-value">{selectedTask.resident_nickname || '\u2014'}</span>
        </div>
        <div className="ehr-dialog__field">
          <span className="ehr-dialog__field-label">Care Level</span>
          <span className="ehr-dialog__field-value">{selectedTask.ehr_level_of_care_id || '\u2014'}</span>
        </div>
        <div className="ehr-dialog__field">
          <span className="ehr-dialog__field-label">Phone</span>
          <span className="ehr-dialog__field-value">{selectedTask.resident_phone || '\u2014'}</span>
        </div>
        <div className="ehr-dialog__field">
          <span className="ehr-dialog__field-label">Email</span>
          <span className="ehr-dialog__field-value">{selectedTask.resident_email || '\u2014'}</span>
        </div>
        <div className="ehr-dialog__field">
          <span className="ehr-dialog__field-label">EHR ID</span>
          <span className="ehr-dialog__field-value">{selectedTask.ehr_resident_id || '\u2014'}</span>
        </div>
      </div>
    );
  };

  /** Render the dialog body for move confirmation */
  const renderMoveBody = () => {
    if (!selectedTask) return null;
    return (
      <p className="ehr-dialog__confirm-text">
        Are you sure you wish to move <strong>{selectedTask.resident_name}</strong> from{' '}
        <strong>{selectedTask.old_area_name}</strong> to <strong>{selectedTask.area_name}</strong>?
      </p>
    );
  };

  /** Render the dialog body for remove confirmation */
  const renderRemoveBody = () => {
    if (!selectedTask) return null;
    return (
      <p className="ehr-dialog__confirm-text">
        Are you sure you wish to confirm moving out <strong>{selectedTask.resident_name}</strong> from{' '}
        <strong>{selectedTask.area_name || selectedTask.old_area_name}</strong>?
      </p>
    );
  };

  /** Render the dialog body for reject confirmation */
  const renderRejectBody = () => {
    const vendorName = getEhrInfo(campusId).vendor_name || 'the EHR system';
    return (
      <p className="ehr-dialog__confirm-text">
        Are you sure you wish to reject this task? New changes within <strong>{vendorName}</strong> will
        be necessary to recreate the task.
      </p>
    );
  };

  /** Render the dialog body for user-remove confirmation */
  const renderUserRemoveBody = () => {
    if (!selectedTask) return null;
    const name = selectedTask.display_name || selectedTask.username;
    return (
      <p className="ehr-dialog__confirm-text">
        Are you sure you wish to remove user <strong>{name}</strong>?
      </p>
    );
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <>
    <NavigationV2 />
    <div className="nav2-main">
    <div className="dp-page">
      {/* Toast */}
      {toast ? (
        <div
          className={`dp-toast ${toast.type === 'success' ? 'dp-toast--success' : 'dp-toast--error'}`}
          role="status"
          aria-live="polite"
        >
          {toast.type === 'success' ? (
            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          )}
          <span>{toast.message}</span>
          <button
            type="button"
            onClick={() => { clearTimeout(toastTimerRef.current); setToast(null); }}
            className="dp-toast__close"
            aria-label="Dismiss"
          >
            <svg className="dp-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : null}

      {/* ============================================================
          PAGE HEADER
          ============================================================ */}
      <div className="dp-page-header">
        <div className="dp-page-header__inner">
          <div className="dp-breadcrumb-row">
            <div className="dp-breadcrumb">
              <span className="dp-breadcrumb__text">Tasks</span>
              <svg className="dp-icon-md dp-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className="dp-breadcrumb__current">EHR</span>
            </div>
            <div className="dp-campus-row">
              <label htmlFor="dp-ehr-campus-selector" className="dp-campus-label">Campus:</label>
              {campusList.length > 1 ? (
                <select
                  id="dp-ehr-campus-selector"
                  value={campusId}
                  onChange={handleCampusChange}
                  className="dp-campus-select"
                >
                  {campusList.map((c) => (
                    <option key={c.campus_id} value={c.campus_id}>
                      {c.campus_name}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="dp-campus-name">{campusName}</span>
              )}
            </div>
          </div>
          <h1 className="dp-page-title">EHR Tasks<EhrInfoTip /></h1>
        </div>
      </div>

      {/* ============================================================
          PARENT TAB BAR — Residents | Users
          Always rendered when at least one parent is enabled, with
          individual tabs gated by ehr_resident / ehr_user.
          ============================================================ */}
      {showResidentsTab || showUsersTab ? (
        <div className="ehr-tab-bar" role="tablist">
          <div className="ehr-tab-bar__inner">
            {showResidentsTab ? (
              <button
                type="button"
                role="tab"
                aria-selected={parentTab === 'residents'}
                className={`ehr-tab ${parentTab === 'residents' ? 'is-active' : ''}`}
                onClick={() => handleParentTabChange('residents')}
              >
                Residents
                {residentsTotal > 0 ? (
                  <span className="ehr-tab__badge">{residentsTotal}</span>
                ) : null}
              </button>
            ) : null}
            {showUsersTab ? (
              <button
                type="button"
                role="tab"
                aria-selected={parentTab === 'users'}
                className={`ehr-tab ${parentTab === 'users' ? 'is-active' : ''}`}
                onClick={() => handleParentTabChange('users')}
              >
                Users
                {usersTotal > 0 ? (
                  <span className="ehr-tab__badge">{usersTotal}</span>
                ) : null}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* ============================================================
          SUBTAB BAR — segmented control for resident or user subtabs
          ============================================================ */}
      <div className="ehr-subtab-row">
        <div className="pp-subtab-toggle" role="tablist">
          {(parentTab === 'residents' ? RESIDENT_TABS : USER_TABS).map((tab) => {
            const count = (rawDataForTab[tab.key] || []).length;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`pp-subtab-btn${isActive ? ' is-active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {count > 0 ? (
                  <span className="ehr-subtab-badge">{count}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* ============================================================
          CONTENT
          ============================================================ */}
      <div className="dp-content">
        {/* Controls */}
        <div className="dp-controls">
          <div className="dp-controls__left">
            <div className="dp-search">
              <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                type="search"
                placeholder="Search tasks..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="dp-search__input"
                aria-label="Search EHR tasks"
              />
            </div>
          </div>
        </div>

        {/* Active filter chips */}
        <ActiveFilterBar
          filters={currentTab.filters}
          columnLabels={columnLabels}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />

        {/* Table */}
        {loading ? (
          <TableSkeleton />
        ) : paginated.length === 0 ? (
          <div className="ehr-empty">
            <p className="ehr-empty__text">
              {ALL_TABS.find((t) => t.key === activeTab)?.emptyMsg || 'No tasks found'}
            </p>
          </div>
        ) : (
          <EhrTaskTable
            columnDefs={TAB_COLUMNS[activeTab]}
            data={paginated}
            allData={rawDataForTab[activeTab]}
            sortColumn={currentTab.sortColumn}
            sortOrder={currentTab.sortOrder}
            onSort={handleSort}
            filters={currentTab.filters}
            onFilterChange={handleFilterChange}
            onRowClick={handleRowClick}
            tzOffset={tzOffset}
          />
        )}

        {/* Pagination */}
        {!loading && totalFiltered > 0 ? (
          <div className="dp-pagination">
            <p className="dp-pagination__info">
              Showing <span className="dp-pagination__emphasis">{showingStart}</span>&ndash;
              <span className="dp-pagination__emphasis">{showingEnd}</span> of{' '}
              <span className="dp-pagination__emphasis">{totalFiltered}</span> tasks
            </p>
            <div className="dp-pagination__controls">
              <div className="dp-per-page">
                <label htmlFor="dp-ehr-per-page" className="dp-per-page__label">Rows per page:</label>
                <select
                  id="dp-ehr-per-page"
                  value={perPage}
                  onChange={(e) => setPerPage(parseInt(e.target.value, 10))}
                  className="dp-per-page__select"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={300}>300</option>
                </select>
              </div>
              <nav className="dp-page-nav" aria-label="EHR tasks pagination">
                <button
                  type="button"
                  onClick={() => updateTabState('page', Math.max(1, safePage - 1))}
                  disabled={safePage <= 1}
                  className="dp-page-btn"
                  aria-label="Previous page"
                >
                  <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                </button>
                {pageNumbers.map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => updateTabState('page', num)}
                    className={`dp-page-num ${num === safePage ? 'is-active' : ''}`}
                    aria-label={`Page ${num}`}
                    aria-current={num === safePage ? 'page' : undefined}
                  >
                    {num}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => updateTabState('page', Math.min(totalPages, safePage + 1))}
                  disabled={safePage >= totalPages}
                  className="dp-page-btn"
                  aria-label="Next page"
                >
                  <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        ) : null}
      </div>

      {/* ============================================================
          DIALOGS
          ============================================================ */}

      {/* Add (Move-in) approval dialog */}
      {activeDialog === 'add' ? (
        <ApproveAddDialog
          task={selectedTask}
          groups={groups}
          onApprove={handleApproveAdd}
          onReject={handleOpenReject}
          onClose={handleCloseDialog}
          submitting={submitting}
        />
      ) : null}

      {/* Edit (Update Info) approval dialog */}
      {activeDialog === 'edit' ? (
        <ConfirmActionDialog
          isOpen
          onClose={handleCloseDialog}
          title="Approve Resident Update"
          onConfirm={handleApproveEdit}
          confirmLabel="Approve"
          onReject={() => handleOpenReject(selectedTask)}
          rejectLabel="Reject"
          submitting={submitting}
        >
          {renderEditBody()}
        </ConfirmActionDialog>
      ) : null}

      {/* Move (Relocate) confirmation dialog */}
      {activeDialog === 'move' ? (
        <ConfirmActionDialog
          isOpen
          onClose={handleCloseDialog}
          title="Confirm Resident Relocate"
          onConfirm={handleApproveMove}
          confirmLabel="Approve"
          onReject={() => handleOpenReject(selectedTask)}
          rejectLabel="Reject"
          submitting={submitting}
        >
          {renderMoveBody()}
        </ConfirmActionDialog>
      ) : null}

      {/* Remove (Move-out) confirmation dialog */}
      {activeDialog === 'remove' ? (
        <ConfirmActionDialog
          isOpen
          onClose={handleCloseDialog}
          title="Confirm Resident Move-out"
          onConfirm={handleApproveRemove}
          confirmLabel="Approve"
          onReject={() => handleOpenReject(selectedTask)}
          rejectLabel="Reject"
          submitting={submitting}
        >
          {renderRemoveBody()}
        </ConfirmActionDialog>
      ) : null}

      {/* User Add approval dialog */}
      {activeDialog === 'user-add' ? (
        <ApproveAddUserDialog
          task={selectedTask}
          roles={roles}
          userGroups={userGroups}
          onApprove={handleApproveUserAdd}
          onReject={() => handleOpenReject(selectedTask)}
          onClose={handleCloseDialog}
          submitting={submitting}
        />
      ) : null}

      {/* User Remove confirmation dialog */}
      {activeDialog === 'user-remove' ? (
        <ConfirmActionDialog
          isOpen
          onClose={handleCloseDialog}
          title="Confirm Remove User"
          onConfirm={handleApproveUserRemove}
          confirmLabel="Approve"
          onReject={() => handleOpenReject(selectedTask)}
          rejectLabel="Reject"
          submitting={submitting}
        >
          {renderUserRemoveBody()}
        </ConfirmActionDialog>
      ) : null}

      {/* Reject confirmation dialog */}
      {activeDialog === 'reject' ? (
        <ConfirmActionDialog
          isOpen
          onClose={handleCancelReject}
          title="Confirm Task Rejection"
          onConfirm={handleConfirmReject}
          confirmLabel="Yes"
          submitting={submitting}
        >
          {renderRejectBody()}
        </ConfirmActionDialog>
      ) : null}

    </div>
    </div>
    </>
  );
};

export default TasksEhrPage;
